import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useCRMAuth } from './CRMAuthContext';

const MarketplaceContext = createContext();

export const useMarketplace = () => {
  const context = useContext(MarketplaceContext);
  if (!context) {
    throw new Error('useMarketplace must be used within a MarketplaceProvider');
  }
  return context;
};

export const MarketplaceProvider = ({ children }) => {
  const { user } = useAuth();
  const { makeAuthenticatedRequest, isLoading: crmAuthLoading, isAuthenticated: crmAuthenticated } = useCRMAuth();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showroom, setShowroom] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // BUGFIX (showroom "flash then flip to create"): showroomChecked is true only after a showroom
  // fetch DEFINITIVELY resolves (HTTP 200 or 404). The create-showroom UI is gated on it so a
  // transient/racing fetch can never flip an already-loaded showroom to the "create" page.
  const [showroomChecked, setShowroomChecked] = useState(false);
  const showroomFetchSeq = useRef(0);

  // API Functions
  const createShowroom = async (showroomData) => {
    if (!user?.brandId) {
      throw new Error('User business ID is required to create showroom');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest('/api/v1/marketplace/showrooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(showroomData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to create showroom: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();

      // Fetch the created showroom
      await fetchShowroom(user.brandId);

      return result;
    } catch (error) {
      console.error('Error creating showroom:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchShowroom = async (businessId) => {
    if (!businessId) return null;

    const seq = ++showroomFetchSeq.current;   // stale-race guard: only the latest fetch may apply
    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/showrooms/${businessId}`,
        {
          credentials: 'include',
        }
      );

      // A newer fetchShowroom started while this one was in flight — discard this stale result so
      // it cannot clobber the newer one (root cause of the "flash correct page then flip to create").
      if (seq !== showroomFetchSeq.current) return null;

      if (response.ok) {
        const data = await response.json();
        // The server now answers 200 with `exists: false` for a tenant that has no showroom — the
        // normal state of every newly onboarded tenant, which used to arrive as a 404 the browser
        // logged as an error on every page load. Treat it as the same definitive empty state.
        if (data && data.exists === false) {
          setShowroom(null);
          setShowroomChecked(true);
          return null;
        }
        setShowroom(data);
        setShowroomChecked(true);
        return data;
      }

      if (response.status === 404) {
        // Legacy contract: older backends answered 404 for "no showroom". Kept so a newer client
        // stays correct against an older revision during a canary.
        setShowroom(null);
        setShowroomChecked(true);
        return null;
      }

      throw new Error(`Failed to fetch showroom: ${response.status}`);
    } catch (error) {
      // Only the latest fetch surfaces an error, and we DO NOT clear an already-loaded showroom on a
      // transient error — erasing a good showroom is what flipped the page to the create UI.
      if (seq === showroomFetchSeq.current) {
        console.error('MarketplaceContext: Error fetching showroom:', error);
        setError(error.message);
      }
      return null;
    } finally {
      if (seq === showroomFetchSeq.current) setLoading(false);
    }
  };

  const updateShowroom = async (businessId, showroomData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/showrooms/${businessId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(showroomData),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update showroom: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();

      // Refresh showroom data
      await fetchShowroom(businessId);

      return result;
    } catch (error) {
      console.error('Error updating showroom:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createProduct = async (productData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest('/api/v1/marketplace/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to create product: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();

      // Refresh products list
      await fetchBusinessProducts();

      return result;
    } catch (error) {
      console.error('Error creating product:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (productId, productData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/products/${productId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(productData),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update product: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();

      // Refresh products list
      await fetchBusinessProducts();

      return result;
    } catch (error) {
      console.error('Error updating product:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/products/${productId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to delete product: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();

      // Refresh products list from backend (no optimistic/local-only removal)
      await fetchBusinessProducts();

      return result;
    } catch (error) {
      console.error('Error deleting product:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // M14 canonical media upload: signed upload-url -> PUT to GCS -> confirm -> persisted object key.
  // Tenant/business identity comes from the backend (authed staff), never the client. Never persists
  // blob:/local/public URLs — the returned object key is resolved to a short-TTL signed URL on read.
  const uploadMedia = async (file) => {
    // 1) request a tenant-scoped signed upload URL (422 = unsupported MIME / oversize)
    const uRes = await makeAuthenticatedRequest('/api/v1/media/upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ filename: file.name, content_type: file.type, size: file.size }),
    });
    if (!uRes.ok) {
      const t = await uRes.text().catch(() => '');
      if (uRes.status === 422) throw new Error('Unsupported file type or file too large.');
      if (uRes.status === 403) throw new Error('You do not have permission to upload media.');
      throw new Error(`Could not start upload (${uRes.status}). ${t}`.trim());
    }
    const info = await uRes.json();
    const { object, upload_url, content_type } = info;
    // 2) PUT the binary directly to the signed GCS URL (no Authorization header on a signed URL)
    const putRes = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': content_type || file.type },
      body: file,
    });
    if (!putRes.ok) throw new Error(`File upload failed (${putRes.status}).`);
    // 3) confirm -> canonical persisted object key (showroom/{business_id}/...)
    const cRes = await makeAuthenticatedRequest('/api/v1/media/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ object }),
    });
    if (!cRes.ok) {
      const t = await cRes.text().catch(() => '');
      throw new Error(`Upload could not be finalized (${cRes.status}). ${t}`.trim());
    }
    const confirmed = await cRes.json();
    return confirmed.object;  // canonical object key — safe to attach to a product
  };

  const replyToInquiry = async (inquiryId, replyMessage) => {
    if (!user?.brandId) {
      throw new Error('Business ID is required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/inquiries/${inquiryId}/reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            reply_message: replyMessage,
            business_id: user.brandId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Reply API error:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText,
        });
        throw new Error(
          `Failed to send reply: ${response.status} - ${errorText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending reply:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const respondToReview = async (reviewId, responseMessage) => {
    if (!user?.brandId) {
      throw new Error('Business ID is required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/reviews/${reviewId}/respond`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            response_message: responseMessage,
            business_id: user.brandId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response API error:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText,
        });
        throw new Error(
          `Failed to post response: ${response.status} - ${errorText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error posting response:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (searchParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();

      if (searchParams.query) queryParams.append('query', searchParams.query);
      if (searchParams.category_id) queryParams.append('category_id', searchParams.category_id);
      if (searchParams.business_id) queryParams.append('business_id', searchParams.business_id);
      if (searchParams.location) queryParams.append('location', searchParams.location);
      if (searchParams.limit) queryParams.append('limit', searchParams.limit);
      if (searchParams.offset) queryParams.append('offset', searchParams.offset);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/products/search?${queryParams.toString()}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to search products: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      setSearchResults(data.products || []);
      return data;
    } catch (error) {
      console.error('Error searching products:', error);
      setError(error.message);
      return { products: [], total: 0 };
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessProducts = async () => {
    if (!user?.brandId) return [];

    try {
      const data = await searchProducts({ business_id: user.brandId });
      setProducts(data.products || []);
      return data.products || [];
    } catch (error) {
      console.error('Error fetching business products:', error);
      return [];
    }
  };

  const getProduct = async (productId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/products/${productId}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to get product: ${response.status} - ${errorText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting product:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/categories`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch categories: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      setCategories(data.categories || []);
      return data.categories || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const addToFavorites = async (productId, businessId) => {
    if (!user?.customer_id) {
      throw new Error('Customer login required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/favorites`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            customer_id: user.customer_id,
            product_id: productId,
            business_id: businessId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to add to favorites: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();

      await fetchFavorites();
      return result;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    if (!user?.customer_id) return [];

    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/favorites/${user.customer_id}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch favorites: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      setFavorites(data.favorites || []);
      return data.favorites || [];
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setError(error.message);
      return [];
    }
  };

  const createReview = async (reviewData) => {
    if (!user?.customer_id) {
      throw new Error('Customer login required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/reviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            ...reviewData,
            customer_id: user.customer_id,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to create review: ${response.status} - ${errorText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating review:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (entityType, entityId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/reviews/${entityType}/${entityId}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch reviews: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      return data.reviews || [];
    } catch (error) {
      console.error('Error fetching reviews:', error);
      return [];
    }
  };

  const geocodeAddress = async (address) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/location/geocode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ address }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to geocode address: ${response.status} - ${errorText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error geocoding address:', error);
      throw error;
    }
  };

  const reverseGeocode = async (latitude, longitude) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/marketplace/location/reverse-geocode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ latitude, longitude }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to reverse geocode: ${response.status} - ${errorText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      throw error;
    }
  };

  // Extract stable primitives from the user object so the effect below does not
  // re-fire on every login() call that creates a new object reference with identical values.
  const userBrandId = user?.brandId ?? null;
  const userRole = user?.role ?? null;
  const userCustomerId = user?.customer_id ?? null;

  // Ref used only for instrumentation — tracks previous brandId to log transitions.
  const prevBrandIdRef = useRef(undefined);

  // Initialize data when user identity changes — gate on Firebase-validated auth, not localStorage.
  useEffect(() => {
    // Wait for Firebase onAuthStateChanged to complete before trusting any user identity.
    if (crmAuthLoading) return;
    // SuperAdmin session — CRM tenant preload must not run; SuperAdmin has its own marketplace scope.
    if (localStorage.getItem('superadmin_session')) return;
    if (!crmAuthenticated) return;

    if (userRole) {
      console.log('[Marketplace] useEffect triggered — brandId:', userBrandId, 'role:', userRole, 'prevBrandId:', prevBrandIdRef.current);
      prevBrandIdRef.current = userBrandId;

      fetchCategories().catch(err => {
        console.warn('Failed to fetch categories:', err);
      });

      if (userRole === 'customer' && userCustomerId) {
        fetchFavorites().catch(err => {
          console.warn('Failed to fetch favorites:', err);
        });
      }

      const businessRoles = ['admin', 'manager', 'group-admin'];
      if (businessRoles.includes(userRole) && userBrandId) {
        console.log('[Marketplace] fetchShowroom triggered for brandId:', userBrandId);
        fetchShowroom(userBrandId).catch(err => {
          console.warn('Failed to fetch showroom:', err);
        });
        fetchBusinessProducts().catch(err => {
          console.warn('Failed to fetch business products:', err);
        });
      }
    }
  }, [userBrandId, userRole, userCustomerId, crmAuthLoading, crmAuthenticated]);

  const contextValue = {
    // State
    products,
    categories,
    showroom,
    showroomChecked,
    favorites,
    searchResults,
    loading,
    error,

    // Actions
    createShowroom,
    fetchShowroom,
    updateShowroom,
    createProduct,
    updateProduct,
    deleteProduct,
    uploadMedia,
    searchProducts,
    fetchBusinessProducts,
    getProduct,
    fetchCategories,
    addToFavorites,
    fetchFavorites,
    createReview,
    fetchReviews,
    geocodeAddress,
    reverseGeocode,
    replyToInquiry,
    respondToReview,
    
    // Utilities
    setError,
    setLoading
  };

  return (
    <MarketplaceContext.Provider value={contextValue}>
      {children}
    </MarketplaceContext.Provider>
  );
};

export { MarketplaceContext };